import { useRef, useState } from "react";
import { postUpdateBookingV4 } from "../../../../../APIs/Booking/UpdateBooking";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { useUserContext } from "../../../../../hooks/useUserContext";
import Loading from "../../../../../utils/Loading";
import { TimePicker } from "../../../../blocks/time-picker";
import { EDSButton } from "../../../../eds/EDSButton";
import { Input } from "../../../../ui/input";
import MentionInput from "./MentionInput";
import NoteRow from "./NoteRow";

const sanitiseNote = (note: string): string =>
  note.trim().replace(/[<>]/g, "").substring(0, 900);

const SelectedBookingNotes = () => {
  const { selectedBookingV4, setDiaryContext } = useDiaryContext();
  const { user } = useUserContext();
  const scrollRef = useRef<HTMLDivElement>(null);
  const addedFlag = useRef<boolean>(false);
  const [selectedNote, setSelectedNote] = useState<string>("Run-Sheet-Note");
  const [newNote, setNewNote] = useState<string>("");
  const [runSheetTime, setRunSheetTime] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const runSheetNotes = selectedBookingV4?.comments ?? [];
  const internalNotes = selectedBookingV4?.admin_notes ?? [];
  const noteDescription = selectedNote.split("-").join(" ").toLowerCase();
  const data =
    selectedNote === "Run-Sheet-Note"
      ? runSheetNotes
      : internalNotes.map((d) => ({
          ...d,
          comment: d.note,
          at_time: undefined
        }));

  const notes = [
    {
      id: "Run-Sheet-Note",
      label: `Run Sheet Notes (${runSheetNotes?.length})`
    },
    {
      id: "Internal-Note",
      label: `Internal Notes (${internalNotes?.length})`
    }
  ];

  if (addedFlag.current) {
    addedFlag.current = false;

    if (scrollRef?.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth"
      });
    }
  }

  const handleAddNote = async () => {
    if (!selectedBookingV4 || isSubmitting) return;

    const sanitisedNote = sanitiseNote(newNote);
    if (!sanitisedNote) return;

    setIsSubmitting(true);

    try {
      const updatedRunSheetNotes =
        selectedNote === "Run-Sheet-Note"
          ? [
              ...runSheetNotes,
              {
                full_name: `${user?.first_name} ${user?.last_name}`,
                time: new Date(),
                comment: sanitisedNote,
                at_time: runSheetTime
              }
            ]
          : runSheetNotes;

      const updatedInternalNotes =
        selectedNote === "Internal-Note"
          ? [
              ...internalNotes,
              {
                full_name: `${user?.first_name} ${user?.last_name}`,
                time: new Date(),
                note: sanitisedNote
              }
            ]
          : internalNotes;

      if (selectedNote === "Run-Sheet-Note") {
        setRunSheetTime("");
      }

      setNewNote("");

      const response = await postUpdateBookingV4({
        ...selectedBookingV4,
        comments: updatedRunSheetNotes,
        admin_notes: updatedInternalNotes,
        // Strip preorders to prevent the Core API from re-processing them (see AB#2210242)
        preorders: undefined
      });

      addedFlag.current = true;

      setDiaryContext({
        selectedBookingV4: {
          ...selectedBookingV4,
          ...response
        }
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex h-full p-4">
      <div className="flex flex-col w-full mt-4 rounded-lg bg-background">
        <div className="flex w-full flex-wrap overflow-hidden mb-0.5 rounded-t-lg bg-background">
          {notes.map((note) => (
            <div
              className={`flex-1 inline-flex items-center justify-center p-2 text-base font-semibold hover:bg-muted transition cursor-pointer ${
                selectedNote === note.id ? "bg-muted" : ""
              }`}
              key={`Note-${note.id}`}
              role="tab"
              tabIndex={0}
              aria-selected={selectedNote === note.id}
              onClick={() => {
                setSelectedNote(note.id);
                scrollRef?.current?.scrollTo({
                  top: 0
                });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSelectedNote(note.id);
                  scrollRef?.current?.scrollTo({
                    top: 0
                  });
                }
              }}
            >
              <span>{note.label}</span>
            </div>
          ))}
        </div>
        <div className="flex-1 flex flex-col relative min-h-10">
          {data.length === 0 && selectedBookingV4 && (
            <span className="absolute inset-x-0 top-1/2 -translate-y-1/2 text-center text-muted-foreground">
              {`No ${noteDescription}s have been added to this booking.`}
            </span>
          )}
          <div className="flex-1 overflow-y-auto relative" ref={scrollRef}>
            {!selectedBookingV4 && <Loading />}
            {data.map((d, i) => (
              <NoteRow
                key={`${d.full_name}-${d.time instanceof Date ? d.time.toISOString() : i}`}
                title={d.full_name}
                added={d.time}
                note={d.comment}
                runSheetTime={d.at_time}
              />
            ))}
          </div>
        </div>
        <div className="border-t border-border flex items-center gap-2 py-4">
          <div className="flex items-center gap-2 w-full">
            {selectedNote === "Run-Sheet-Note" && (
              <div className="pr-4">
                <TimePicker
                  value={runSheetTime}
                  placeholder="Time"
                  format={user?.locale === "en-US" ? "12h" : "24h"}
                  onChange={(value) => setRunSheetTime(value)}
                />
              </div>
            )}
            {selectedNote === "Internal-Note" ? (
              <MentionInput
                value={newNote}
                onChange={setNewNote}
                placeholder={`Type ${noteDescription}...`}
                maxLength={900}
                className="flex-1"
              />
            ) : (
              <Input
                value={newNote}
                placeholder={`Type ${noteDescription}...`}
                maxLength={900}
                onChange={(e) => setNewNote(e.target.value)}
                className="flex-1"
              />
            )}
          </div>
          <EDSButton
            variant="default"
            disabled={newNote.length === 0 || isSubmitting}
            onClick={handleAddNote}
          >
            {isSubmitting ? "Adding..." : "Add"}
          </EDSButton>
        </div>
      </div>
    </div>
  );
};

export default SelectedBookingNotes;
